import React, { useMemo, useState } from "react";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import Loader from "../loader";
import renderIcon from "../../../components/renderIcon";
import AiContentSlot from "../../../components/AiContentSlot";
import HebcalService from "../../../services/hebcal";
import { restPeriodTitle, restPeriodRowLabel } from "../../../services/restPeriodDisplay";
import { applyLang } from "../../../i18n";
import { ASSET_URL } from "../../../config";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import logger from "../../../services/logger";
import { formatShabbatSessionDateRange } from "../../../lib/shabbatDateRange";

/**
 * Page publique « Shabbat » (thème 2026) — module généralisé hors-tourisme.
 *
 * Réécriture au style 2026 de web/themes/Travel/Chabbat/index.js. Contenu :
 *   - Horaires du prochain Shabbat (allumage bougies / sortie / parasha) calculés
 *     CÔTÉ CLIENT via HebcalService à partir de org.lat/long (aucune donnée API).
 *   - Sessions/repas de Shabbat + liens d'inscription, depuis le pe_groupe système
 *     d'extension "SHABBAT" exposé sous org.eventGroupesByExtension["SHABBAT"].
 *   - Intro IA optionnelle (groupe.aiContentBlockId).
 *
 * PAS d'Érouv (hors périmètre + dépendance travel.jewishInfo supprimée).
 * Le gate d'affichage (org.shabbatModule.active) est déjà fait côté API : la page
 * n'existe dans org.pages que si le module est actif.
 *
 * Couleurs 100% dérivées des tokens du thème (--primary, --surface, --ink…).
 */

const SHABBAT_EXTENSION = "SHABBAT";

const styles = `
.t26-shab { max-width: var(--container); margin-inline: auto; padding: 0 clamp(16px,4vw,32px) clamp(40px,6vw,80px); }

/* ── HERO image optionnel ── */
.t26-shab__hero {
  position: relative;
  height: clamp(220px, 34vw, 420px);
  overflow: hidden;
  border-radius: var(--r-lg);
  margin-bottom: clamp(24px, 4vw, 40px);
}
.t26-shab__hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.t26-shab__hero-wash { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,.5) 100%); }
.t26-shab__hero-title {
  position: absolute; inset: auto 0 24px 0; text-align: center; color: #fff;
  font-family: var(--font-display); font-size: clamp(26px, 4vw, 46px); font-weight: 700;
  padding: 0 24px; text-shadow: 0 2px 16px rgba(0,0,0,.35);
}

/* ── HORAIRES ── */
.t26-shab__times { text-align: center; margin: 0 auto clamp(28px,4vw,44px); max-width: 720px; }
.t26-shab__times-title { font-family: var(--font-display); font-size: clamp(20px,3vw,30px); font-weight: 700; color: var(--ink); margin: 0 0 6px; }
.t26-shab__times-parasha { font-size: clamp(15px,2vw,20px); font-weight: 600; color: var(--secondary); margin: 0; }
.t26-shab__times-date { color: var(--ink-3); font-size: 14px; margin-top: 4px; }
.t26-shab__times-grid { display: flex; justify-content: center; gap: 14px; margin-top: 22px; flex-wrap: wrap; }
.t26-shab__tile {
  background: color-mix(in oklab, var(--primary) 8%, var(--surface));
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  padding: 20px 26px; min-width: 156px; text-align: center;
}
.t26-shab__tile-icon { color: var(--primary); margin-bottom: 8px; display: inline-flex; }
.t26-shab__tile-label { font-size: 12px; text-transform: uppercase; letter-spacing: .1em; color: var(--ink-3); margin-bottom: 4px; }
.t26-shab__tile-value { font-family: var(--font-display); font-size: 24px; font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }
.t26-shab__tile-date { font-size: 12px; color: var(--ink-3); margin-top: 5px; }

/* ── INTRO IA ── */
.t26-shab__intro { margin: 0 auto clamp(24px,4vw,36px); max-width: var(--container); }

/* ── SESSIONS ── */
.t26-shab__sessions-wrap { max-width: 860px; margin-inline: auto; }
.t26-shab__section-label {
  font-size: 12px; text-transform: uppercase; letter-spacing: .12em;
  color: var(--ink-3); font-weight: 700; margin-bottom: 14px;
}
.t26-shab__sessions { display: flex; flex-direction: column; gap: 8px; }
.t26-shab__session {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 16px; border: 1px solid var(--line); border-radius: var(--r-md);
  background: var(--surface); cursor: pointer; transition: border-color .15s, background .15s;
}
.t26-shab__session:hover { border-color: var(--primary); background: color-mix(in oklab, var(--primary) 5%, var(--surface)); }
.t26-shab__session--selected { border: 2px solid var(--primary); background: color-mix(in oklab, var(--primary) 8%, var(--surface)); }
.t26-shab__session--locked { opacity: .55; cursor: not-allowed; }
.t26-shab__session-left { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.t26-shab__session-date { font-weight: 700; font-size: 15px; color: var(--ink); display: flex; align-items: center; gap: 6px; }
.t26-shab__session-label { font-size: 13px; color: var(--ink-3); }
.t26-shab__links { margin: 4px 0 8px 12px; }
.t26-shab__link {
  display: flex; align-items: center; justify-content: space-between;
  padding: 13px 18px; border-radius: var(--r-md);
  background: var(--primary); color: var(--primary-contrast) !important;
  text-decoration: none !important; font-weight: 600; margin-bottom: 8px;
  transition: transform .15s, box-shadow .15s;
}
.t26-shab__link:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
.t26-shab__link span, .t26-shab__link svg { color: var(--primary-contrast) !important; }
.t26-shab__empty {
  padding: 22px; background: color-mix(in oklab, var(--ink) 3%, var(--surface));
  border: 1px dashed var(--line); border-radius: var(--r-md);
  text-align: center; color: var(--ink-3);
}
`;

/** Heure murale HH:MM dans le fuseau de l'org (pas celui du navigateur). */
function formatTime(d, tz) {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("fr", {
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz || undefined,
    }).format(date);
}

function formatDate(d, locale, tz) {
    return new Intl.DateTimeFormat(locale || "fr", {
        day: "2-digit", month: "2-digit", year: "numeric", timeZone: tz || undefined,
    }).format(d);
}

export default function Shabbat({ context, loading }) {
    // `lang` = OBJET {value} attendu par applyLang (les overlays i18n ne s'appliquent
    // que via lang.value). `langValue` = STRING (locale) pour AiContentSlot / formatage.
    const { org, config, lang, langValue } = context;
    const locale = langValue || "fr";

    // Groupe système (sessions) — exposé pour toute org sous cette clé.
    const groupeRaw = org.eventGroupesByExtension?.[SHABBAT_EXTENSION] || null;
    const groupe = useMemo(() => (groupeRaw ? applyLang(groupeRaw, lang) : null), [groupeRaw, lang]);

    // Sessions ouvertes : fenêtre dateDebut <= now <= dateFin (filtre CLIENT — le
    // serveur renvoie toutes les sessions du groupe, pas de filtre côté API).
    const sessions = useMemo(() => {
        if (!groupe) return [];
        const now = Date.now();
        return (groupe.sessions || [])
            .map(s => applyLang(s, lang))
            .filter(s => {
                const debut = s.dateDebut ? new Date(s.dateDebut).getTime() : 0;
                const fin = s.dateFin ? new Date(s.dateFin).getTime() : Infinity;
                return now >= debut && now <= fin;
            });
    }, [groupe, lang]);

    const [selectedId, setSelectedId] = useState(null);

    // Horaires du prochain Shabbat (entrée + sortie + parasha), calcul client.
    const restPeriod = useMemo(() => {
        if (!org?.lat || !org?.long) return null;
        try {
            const hb = new HebcalService(org);
            return hb.getNextRestPeriod(new Date());
        } catch (e) {
            logger.error({ err: e }, "Hebcal error (Shabbat page)");
            return null;
        }
    }, [org?.lat, org?.long, org?.israel, org?.zmanimShitat, org?.chabadCandleLightingMinutes]);

    const title = groupe?.title || groupe?.label || org.shabbatModule?.label || t`Shabbat à ${org.ville || org.name}`;
    const description = groupe?.description || config?.["meta.description"] || t`Horaires et repas de Shabbat à ${org.ville || org.name}`;
    const heroImageRaw = groupe?.image || org.shabbatModule?.image || null;
    const heroImage = heroImageRaw ? (heroImageRaw.startsWith("http") ? heroImageRaw : `${ASSET_URL}${heroImageRaw}`) : null;

    if (loading) {
        return (
            <>
                <Meta context={context} title={title} description={description} />
                <Loader />
            </>
        );
    }

    return (
        <>
            <Meta context={context} title={title} description={description} image={heroImage} />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            {heroImage ? (
                <div className="t26-shab" style={{ paddingBottom: 0 }}>
                    <section className="t26-shab__hero">
                        <img src={heroImage} alt={title} className="t26-shab__hero-img" />
                        <div className="t26-shab__hero-wash" />
                        <h1 className="t26-shab__hero-title">{title}</h1>
                    </section>
                </div>
            ) : (
                <PageHeader
                    title={t`Shabbat`}
                    titleEm={org.ville || org.name}
                    subtitle={t`Horaires du prochain Shabbat et réservation des repas`}
                    crumbs={[
                        { label: t`Accueil`, url: org.BASE_WEB_URL },
                        { label: t`Shabbat` },
                    ]}
                />
            )}

            <div className="t26-shab">
                {/* HORAIRES */}
                {restPeriod && (
                    <section className="t26-shab__times">
                        <h2 className="t26-shab__times-title">
                            {restPeriodTitle(restPeriod, locale)}
                        </h2>
                        <div className="t26-shab__times-date">
                            {formatDate(restPeriod.start, locale, restPeriod.timeZone)} — {formatDate(restPeriod.end, locale, restPeriod.timeZone)}
                        </div>
                        <div className="t26-shab__times-grid">
                            {restPeriod.schedule.map((item, index) => (
                            <div className="t26-shab__tile" key={index}>
                                <div className="t26-shab__tile-icon">
                                    {renderIcon({ name: item.kind === "exit" ? "IconGlassFull" : "IconCandle", source: "tabler" }, 32)}
                                </div>
                                <div className="t26-shab__tile-label">{restPeriodRowLabel(item, restPeriod)}</div>
                                <div className="t26-shab__tile-value">{formatTime(item.at, restPeriod.timeZone)}</div>
                                <div className="t26-shab__tile-date">{formatDate(item.at, locale, restPeriod.timeZone)}</div>
                            </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* INTRO IA */}
                {groupe?.aiContentBlockId && (
                    <div className="t26-shab__intro">
                        <AiContentSlot blockId={groupe.aiContentBlockId} lang={locale} />
                    </div>
                )}

                {/* SESSIONS */}
                <div className="t26-shab__sessions-wrap">
                    <div className="t26-shab__section-label">
                        <Trans>Choisissez votre Shabbat</Trans>
                    </div>

                    {sessions.length === 0 ? (
                        <div className="t26-shab__empty">
                            <Trans>Aucune session disponible pour le moment.</Trans>
                        </div>
                    ) : (
                        <div className="t26-shab__sessions">
                            {sessions.map(sess => {
                                const isSelected = sess.id === selectedId;
                                const isAvail = sess.active !== false;
                                const cls = [
                                    "t26-shab__session",
                                    isSelected ? "t26-shab__session--selected" : "",
                                    !isAvail ? "t26-shab__session--locked" : "",
                                ].filter(Boolean).join(" ");
                                return (
                                    <React.Fragment key={sess.id}>
                                        <div
                                            className={cls}
                                            onClick={() => isAvail && setSelectedId(isSelected ? null : sess.id)}
                                        >
                                            <div className="t26-shab__session-left">
                                                <div className="t26-shab__session-date">
                                                    {renderIcon({ name: "IconCalendar", source: "tabler" }, 14)}
                                                    {/* Exception module Shabbat : plage vendredi/samedi dérivée du
                                                        samedi de la session. Fallback dateEvent si dateFin absent. */}
                                                    <bdi dir={groupeRaw?.shabbatDateFormat === "numeric" ? "ltr" : "auto"}>{formatShabbatSessionDateRange(sess.dateFin || sess.dateEvent, locale, groupeRaw?.shabbatDateFormat)}</bdi>
                                                </div>
                                                {(sess.title || sess.name) && (
                                                    <div className="t26-shab__session-label">{sess.title || sess.name}</div>
                                                )}
                                            </div>
                                            {!isAvail
                                                ? renderIcon({ name: "IconLock", source: "tabler" }, 16)
                                                : (
                                                    <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: isSelected ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                                        {renderIcon({ name: "IconChevronRight", source: "tabler" }, 18)}
                                                    </span>
                                                )
                                            }
                                        </div>

                                        {isSelected && (
                                            <div className="t26-shab__links">
                                                {(sess.links || []).length === 0 ? (
                                                    <div className="t26-shab__empty">
                                                        <Trans>Aucune inscription disponible pour cette session.</Trans>
                                                    </div>
                                                ) : (
                                                    (sess.links || []).map((link, i) => {
                                                        const tLink = applyLang(link, lang);
                                                        return (
                                                            <a key={i} href={tLink.url} target="_blank" rel="noreferrer" className="t26-shab__link">
                                                                <span>{tLink.name}</span>
                                                                {renderIcon({ name: "IconChevronRight", source: "tabler" }, 18)}
                                                            </a>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
